import type { ComponentInit } from "@scripts/registry";
import { lockScroll } from "@scripts/scrollLock";
import { settle, toMs } from "@scripts/settle";

const TRIGGER = "data-fa-modal-trigger";
const CLOSING = "fa-modal--closing";
const SETTLE_MARGIN = 50;

const dialogFor = (trigger: HTMLElement): HTMLDialogElement | null => {
	const id = trigger.getAttribute(TRIGGER);
	const element = id === null || id === "" ? null : document.getElementById(id);

	return element instanceof HTMLDialogElement ? element : null;
};

const exitMs = (dialog: HTMLDialogElement): number =>
	Math.max(0, ...getComputedStyle(dialog).transitionDuration.split(",").map(toMs));

export const initModal: ComponentInit = (trigger) => {
	const listeners = new AbortController();
	let session: AbortController | undefined;
	let unlock: (() => void) | undefined;

	const authoredHaspopup = trigger.hasAttribute("aria-haspopup");

	if (!authoredHaspopup) trigger.setAttribute("aria-haspopup", "dialog");

	const open = (dialog: HTMLDialogElement) => {
		if (dialog.open) return;

		dialog.showModal();
		/* Not `autofocus`: Chromium ignores it on the dialog and focuses the close button anyway. */
		dialog.focus();
		unlock = lockScroll();
		session = new AbortController();

		const { signal } = session;

		let cancelExit: (() => void) | undefined;

		const requestClose = () => {
			if (cancelExit) {
				cancelExit();
				cancelExit = undefined;
				dialog.close();

				return;
			}

			const ms = exitMs(dialog);

			if (ms === 0) {
				dialog.close();

				return;
			}

			dialog.classList.add(CLOSING);
			cancelExit = settle(dialog, ms + SETTLE_MARGIN, () => {
				cancelExit = undefined;
				dialog.close();
			});
		};

		dialog.addEventListener(
			"click",
			(event) => {
				if (event.target === dialog) requestClose();
			},
			{ signal },
		);

		/* Escape and `form method="dialog"` close natively, so the exit only plays if both are
		   taken over here — and cleanup stays on `close`, which a scripted close still reaches. */
		dialog.addEventListener(
			"cancel",
			(event) => {
				event.preventDefault();
				requestClose();
			},
			{ signal },
		);

		dialog.addEventListener(
			"submit",
			(event) => {
				const form = event.target;

				if (!(form instanceof HTMLFormElement) || form.method !== "dialog") return;

				event.preventDefault();
				requestClose();
			},
			{ signal },
		);

		dialog.addEventListener(
			"close",
			() => {
				cancelExit?.();
				cancelExit = undefined;
				dialog.classList.remove(CLOSING);
				unlock?.();
				unlock = undefined;
				session?.abort();
				session = undefined;
			},
			{ signal },
		);
	};

	trigger.addEventListener(
		"click",
		() => {
			const dialog = dialogFor(trigger);

			if (dialog) open(dialog);
		},
		{ signal: listeners.signal },
	);

	return () => {
		listeners.abort();

		if (session) dialogFor(trigger)?.close();

		session?.abort();
		unlock?.();

		if (!authoredHaspopup) trigger.removeAttribute("aria-haspopup");
	};
};
